import { useState } from "react"
import { X, Palette, Check } from "lucide-react"
import { toast } from "@/clientWeb/components/ui/toast-utils"

interface ChatColorPageProps {
  currentColor?: string
  onClose?: () => void
  onSave?: (color: string) => void
}

const CHAT_COLORS = [
  { id: "default", name: "Default", value: "", color: "" },
  { id: "blue", name: "Blue", value: "#3b82f6", color: "bg-blue-500" },
  { id: "green", name: "Green", value: "#22c55e", color: "bg-green-500" },
  { id: "yellow", name: "Yellow", value: "#eab308", color: "bg-yellow-500" },
  { id: "orange", name: "Orange", value: "#f97316", color: "bg-orange-500" },
  { id: "red", name: "Red", value: "#ef4444", color: "bg-red-500" },
  { id: "pink", name: "Pink", value: "#ec4899", color: "bg-pink-500" },
  { id: "purple", name: "Purple", value: "#a855f7", color: "bg-purple-500" },
  { id: "indigo", name: "Indigo", value: "#6366f1", color: "bg-indigo-500" },
  { id: "teal", name: "Teal", value: "#14b8a6", color: "bg-teal-500" },
  { id: "cyan", name: "Cyan", value: "#06b6d4", color: "bg-cyan-500" },
  { id: "slate", name: "Slate", value: "#64748b", color: "bg-slate-500" },
]

export function ChatColorPage({ currentColor, onClose, onSave }: ChatColorPageProps) {
  const [selectedColor, setSelectedColor] = useState(currentColor || "")

  const handleSave = () => {
    onSave?.(selectedColor)
    const colorName = CHAT_COLORS.find(c => c.value === selectedColor)?.name ?? "Default"
    toast.success({
      title: "Theme color saved",
      description: `Chat color changed to ${colorName}.`,
    })
    // Deliberately still here.
    //
    // Applying a colour and being thrown back to the settings list makes trying a
    // second one a three-click round trip, and the toast confirming the first
    // arrives on a screen that has already gone. The X in the header is the
    // way back, and it is where it always was.
  }

  const handleSelectColor = (value: string) => {
    setSelectedColor(value)
  }

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 text-base font-semibold text-foreground">Chat Color</h2>
        <button
          onClick={handleSave}
          className="px-4 py-1.5 rounded-lg font-medium text-sm text-primary hover:bg-primary/10 transition-colors"
        >
          Save
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Preview */}
        <div className="mb-6 p-4 rounded-xl bg-muted/60 border border-border">
          <p className="text-xs text-muted-foreground mb-3 font-medium">Preview</p>
          <div className="space-y-2">
            {/* Received bubble */}
            <div className="flex items-end gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">J</div>
              <div className="bg-card border border-border/50 rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm text-foreground max-w-[70%]">
                Hey! How's the new feature?
              </div>
            </div>
            {/* Sent bubble */}
            <div className="flex justify-end">
              <div
                className="rounded-2xl rounded-br-sm px-3.5 py-2 text-sm text-white max-w-[70%]"
                // `var(--primary)`, not `hsl(var(--primary))`: this project's tokens
                // are raw colours (`--primary: #0C5A96`), not the HSL triplets that
                // wrapper expects, so `hsl(#0C5A96)` was invalid and the preview
                // bubble lost its colour entirely on "Default".
                style={{ backgroundColor: selectedColor || "var(--primary-default)" }}
              >
                Really well! Almost done 🚀
              </div>
            </div>
          </div>
        </div>

        {/* Color Options */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Choose a color</h3>
          <div className="grid grid-cols-4 gap-3">
            {CHAT_COLORS.map(color => (
              <button
                key={color.id}
                onClick={() => handleSelectColor(color.value)}
                className={`relative aspect-square rounded-xl overflow-hidden transition-all hover:scale-105 ${
                  selectedColor === color.value ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""
                }`}
              >
                {/* "Default" is painted from `--primary-default`, a token the
                    chat-colour override deliberately does not touch. Using
                    `bg-primary` here showed the CURRENT colour under the word
                    "Default" — pick green, reopen, and the default swatch was
                    green too. */}
                <div
                  className={`w-full h-full ${color.color}`}
                  style={color.value ? undefined : { backgroundColor: "var(--primary-default)" }}
                />
                {selectedColor === color.value && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                )}
                <span className="absolute bottom-1 left-1 right-1 text-[10px] font-medium text-foreground/80 bg-background/80 rounded px-1.5 py-0.5 truncate text-center">
                  {color.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-muted/50 rounded-xl">
          <div className="flex items-start gap-3">
            <Palette className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              The selected color will be used for chat bubbles, avatars, and other accent elements in your conversations.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
